import React, { useState } from 'react';
import Footer from './Footer';
import { AgentRole, ChapterLength, ProjectMetadata, StoryBible, StoryMode } from '../src/types';
import { getDisplayChapterCount } from '../src/utils/projectCounts';
import { resolveProjectsLoadPresentation, type ProjectsLoadState } from '../src/utils/projectLoader';
import { useFirebase } from './FirebaseProvider';

interface ProjectDashboardProps {
  projects: ProjectMetadata[];
  projectsLoadState?: ProjectsLoadState;
  projectsLoadError?: string | null;
  onRetryProjectsLoad?: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onUpgradePlan: () => void;
}

const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  projects,
  projectsLoadState = 'success',
  projectsLoadError = null,
  onRetryProjectsLoad,
  onSelect,
  onDelete,
  onNew,
  onUpgradePlan,
}) => {
  const [deleteModal, setDeleteModal] = useState<{isOpen: boolean, projectId: string | null}>({isOpen: false, projectId: null});
  const { user, userData, login, logout } = useFirebase();
  const presentation = resolveProjectsLoadPresentation(projectsLoadState, projects.length);

  return (
    <div className="min-h-screen bg-cv-page flex flex-col p-4 md:p-8 text-cv-primary">
      <div className="max-w-6xl w-full mx-auto">
        <header className="mb-8 md:mb-12 flex flex-col md:flex-row md:justify-between items-start md:items-end gap-6 md:gap-0">
          {/* Header content ... */}
          <div>
            <h1 className="text-4xl font-extrabold text-cv-gold tracking-tight flex items-center gap-3">
              Chronovela
            </h1>
            <p className="text-cv-muted mt-1 font-bold text-xs uppercase tracking-widest">Operated by Skald Haven Inc.</p>
            <p className="text-cv-muted mt-2 font-mono uppercase tracking-widest text-sm">AI manuscript generation platform for authors.</p>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-3 px-4 py-2 bg-cv-panel border border-cv-border rounded-lg hover:border-cv-border-strong transition-colors" type="button">
                  <span className="text-sm text-cv-secondary font-medium tracking-wide flex items-center">
                    <span className="hidden md:inline">Free Preview</span>
                    <span className="md:hidden">Credits</span>
                    <span className="hidden md:inline mx-2 text-cv-disabled">·</span>
                    <span className="hidden md:inline text-cv-muted">Standard <span className="text-cv-gold font-bold">{userData?.standardCredits || 0}</span></span>
                    <span className="hidden md:inline mx-1 text-cv-disabled">·</span>
                    <span className="hidden md:inline text-cv-muted">Long <span className="text-cv-interactive font-bold">{userData?.longCredits || 0}</span></span>
                    <span className="hidden md:inline mx-1 text-cv-disabled">·</span>
                    <span className="hidden md:inline text-cv-muted">Epic <span className="text-cv-warning font-bold">{userData?.epicCredits || 0}</span></span>
                    <span className="hidden md:inline mx-1 text-cv-disabled">·</span>
                    <span className="hidden md:inline text-cv-muted">Rewrite <span className="text-cv-success font-bold">{userData?.rewriteCredits || 0}</span></span>
                  </span>
                  <svg className="w-4 h-4 text-cv-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-64 bg-cv-panel border border-cv-border-strong rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                  <div className="p-4 border-b border-cv-border bg-cv-page/50">
                    <p className="text-xs text-cv-muted font-mono mb-1">SIGNED IN AS</p>
                    <p className="text-sm font-medium text-cv-secondary truncate">{user.email}</p>
                  </div>
                  <div className="p-2 space-y-1">
                    <div className="px-3 py-2 flex justify-between items-center text-sm">
                      <span className="text-cv-muted">Plan</span>
                      <span className="text-cv-primary font-medium capitalize">Free Preview</span>
                    </div>
                    <div className="px-3 py-2 flex justify-between items-center text-sm">
                      <span className="text-cv-muted">Book Credits</span>
                      <span className="text-cv-gold font-bold">{
                        (userData?.standardCredits || 0) + 
                        (userData?.longCredits || 0) + 
                        (userData?.epicCredits || 0)
                      }</span>
                    </div>
                  </div>
                  <div className="p-2 border-t border-cv-border">
                    <button onClick={onUpgradePlan} className="w-full text-left px-3 py-2 text-sm text-cv-interactive hover:bg-cv-gold/10 rounded transition-colors font-medium">
                      Upgrade Plan
                    </button>
                    {typeof window !== 'undefined' && (window as any).aistudio && (
                      <button
                        onClick={async () => {
                          try {
                            await (window as any).aistudio.openSelectKey();
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-cv-muted hover:bg-cv-elevated hover:text-cv-primary rounded transition-colors"
                      >
                        Developer Settings (API Key)
                      </button>
                    )}
                      <a href="/help" className="w-full text-left px-3 py-2 text-sm text-cv-secondary hover:bg-cv-elevated rounded transition-colors mt-1 block">
                        Help Center
                      </a>
                      <button onClick={logout} className="w-full text-left px-3 py-2 text-sm text-cv-danger hover:bg-red-500/10 rounded transition-colors mt-1">
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={login} className="px-4 py-2 bg-cv-elevated hover:bg-cv-elevated text-cv-primary rounded font-bold uppercase tracking-widest text-xs transition-colors border border-cv-border-strong">
                 Sign In
              </button>
            )}
            <button
              onClick={() => {
                if (!user) {
                  alert("Sign in or create an account to start your free preview.");
                  login();
                } else {
                  onNew();
                }
              }}
              className="px-6 py-3 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded-lg font-bold shadow-lg shadow-black/20 transition-all transform hover:scale-105"
            >
              {user ? "Start New Book" : "Start Free Preview"}
            </button>
          </div>
        </header>

        {presentation === 'loading' ? (
          <div className="bg-cv-panel/50 border border-cv-border rounded-2xl p-8 md:p-16 text-center shadow-xl">
            <h2 className="text-2xl font-bold text-cv-primary mb-3 tracking-wide">Loading your library…</h2>
            <p className="text-cv-muted max-w-md mx-auto leading-relaxed">
              Fetching your manuscripts from Chronovela.
            </p>
          </div>
        ) : presentation === 'error' ? (
          <div className="bg-cv-panel/50 border border-red-900/40 rounded-2xl p-8 md:p-16 text-center shadow-xl">
            <h2 className="text-2xl font-bold text-cv-primary mb-3 tracking-wide">Library unavailable</h2>
            <p className="text-cv-secondary max-w-md mx-auto mb-8 leading-relaxed">
              {projectsLoadError || 'Your projects could not be loaded.'}
            </p>
            {onRetryProjectsLoad && (
              <button
                onClick={onRetryProjectsLoad}
                className="px-8 py-3 bg-cv-elevated hover:bg-cv-disabled text-cv-primary rounded-lg font-bold transition-all"
              >
                Try Again
              </button>
            )}
          </div>
        ) : presentation === 'empty' ? (
          <div>
            <div className="bg-cv-panel/50 border border-cv-border rounded-2xl p-8 md:p-16 text-center shadow-xl">
              <div className="w-20 h-20 bg-cv-elevated/50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <svg className="w-10 h-10 text-cv-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-cv-primary mb-3 tracking-wide">No stories yet</h2>
              <p className="text-cv-muted max-w-md mx-auto mb-8 leading-relaxed">
                Create your first Story Blueprint, build a chapter roadmap, and start generating chapters with canon tracking.
              </p>
              <button
                onClick={() => {
                  if (!user) {
                    alert("Sign in or create an account to start your free preview.");
                    login();
                  } else {
                    onNew();
                  }
                }}
                className="px-8 py-3 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded-lg font-bold shadow-lg shadow-black/20 transition-all transform hover:scale-105"
              >
                {user ? "Start New Book" : "Start Free Preview"}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-cv-panel/40 border border-cv-border/60 p-6 rounded-xl hover:bg-cv-panel/60 transition-colors">
                <div className="w-10 h-10 bg-cv-interactive/15 rounded-lg flex items-center justify-center mb-4 border border-cv-interactive/20">
                  <svg className="w-5 h-5 text-cv-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <h3 className="text-lg font-bold text-cv-primary mb-2 tracking-tight">Create a Story Blueprint</h3>
                <p className="text-sm text-cv-muted leading-relaxed">Define your world, characters, genre, tone, and story direction.</p>
              </div>
              <div className="bg-cv-panel/40 border border-cv-border/60 p-6 rounded-xl hover:bg-cv-panel/60 transition-colors">
                <div className="w-10 h-10 bg-cv-gold/10 rounded-lg flex items-center justify-center mb-4 border border-cv-gold/20">
                  <svg className="w-5 h-5 text-cv-interactive" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                </div>
                <h3 className="text-lg font-bold text-cv-primary mb-2 tracking-tight">Plan the Book</h3>
                <p className="text-sm text-cv-muted leading-relaxed">Generate a chapter roadmap before writing begins.</p>
              </div>
              <div className="bg-cv-panel/40 border border-cv-border/60 p-6 rounded-xl hover:bg-cv-panel/60 transition-colors">
                <div className="w-10 h-10 bg-cv-success/15 rounded-lg flex items-center justify-center mb-4 border border-cv-success/20">
                  <svg className="w-5 h-5 text-cv-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </div>
                <h3 className="text-lg font-bold text-cv-primary mb-2 tracking-tight">Write with Continuity</h3>
                <p className="text-sm text-cv-muted leading-relaxed">The engine writes chapters while tracking canon, plot threads, and character details.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {Object.entries(
              projects.reduce((acc, p) => {
                const seriesId = p.seriesId || p.id;
                if (!acc[seriesId]) acc[seriesId] = [];
                acc[seriesId].push(p);
                return acc;
              }, {} as Record<string, ProjectMetadata[]>)
            ).map(([seriesId, seriesProjects]) => {
              const sortedProjects = (seriesProjects as ProjectMetadata[]).sort((a, b) => (a.volume || 1) - (b.volume || 1));
              const latestProject = sortedProjects[sortedProjects.length - 1];
              
              return (
                <div key={seriesId} className="bg-cv-panel/30 rounded-2xl p-6 border border-cv-border/50">
                  <h2 className="text-2xl font-black text-cv-primary mb-6 flex items-center gap-3">
                    <span className="text-cv-interactive">SERIES:</span> {(latestProject.title || 'Untitled').split(':')[0]}
                    <span className="text-xs font-mono bg-cv-elevated text-cv-muted px-2 py-1 rounded-full">{sortedProjects.length} VOLUMES</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sortedProjects.map((project) => (
                      <div 
                        key={project.id}
                        className="group relative bg-cv-panel border border-cv-border rounded-xl overflow-hidden hover:border-cv-interactive/50 transition-all hover:shadow-2xl hover:shadow-blue-900/10 cursor-pointer"
                        onClick={() => onSelect(project.id)}
                      >
                        <div className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <span className="text-xs font-mono px-2 py-1 bg-cv-interactive/15 text-cv-gold rounded uppercase">
                              {project.genre}
                            </span>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModal({ isOpen: true, projectId: project.id });
                              }}
                              className="text-cv-disabled hover:text-cv-danger transition-colors p-1"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <h3 className="text-xl font-bold text-cv-primary mb-2 group-hover:text-cv-gold transition-colors flex items-center justify-between">
                            {project.title || 'Untitled'}
                            <span className="text-[10px] text-cv-interactive font-mono bg-cv-interactive/15 px-2 py-1 rounded">VOL {project.volume || 1}</span>
                          </h3>
                          <div className="space-y-3 mt-6">
                            <div className="flex items-center text-xs text-cv-muted gap-2 font-mono">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                                {getDisplayChapterCount(project, user?.uid)} Chapters generated
                              </div>
                              {project.endingType && (
                                <div className="flex items-center text-[10px] text-cv-interactive/70 gap-2 font-mono uppercase tracking-widest mt-1">
                                  Ending: {project.endingType}
                                </div>
                              )}
                            <div className="flex items-center text-xs text-cv-muted gap-2 font-mono">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              Last updated: {new Date(project.lastUpdated).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-cv-elevated group-hover:bg-cv-success transition-colors"></div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-cv-panel border border-cv-border-strong rounded-xl p-6 w-[400px] max-w-full shadow-2xl flex flex-col gap-4">
            <h2 className="text-lg font-black text-cv-primary uppercase tracking-wider flex items-center gap-2 text-cv-danger">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Confirm Deletion
            </h2>
            <p className="text-sm text-cv-muted">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setDeleteModal({ isOpen: false, projectId: null })}
                className="px-4 py-2 rounded text-xs font-bold uppercase text-cv-muted hover:text-cv-primary hover:bg-cv-elevated transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteModal.projectId) {
                    onDelete(deleteModal.projectId);
                  }
                  setDeleteModal({ isOpen: false, projectId: null });
                }}
                className="px-4 py-2 rounded text-xs font-black uppercase bg-cv-danger text-cv-page hover:bg-cv-danger/90 transition-colors shadow-lg shadow-black/20"
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ProjectDashboard;